import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type {
  IServiceProviderReceiptUploadPhoenixApi,
  ReceiptImageAnalysis,
} from './api';
import { ServiceProviderReceiptUploadPhoenixApi } from './api';
import type { UseQueryResult } from '@tanstack/react-query';

const ApiContext = createContext<
  IServiceProviderReceiptUploadPhoenixApi | undefined
>(undefined);

const {
  useApi: useServiceProviderReceiptUploadPhoenixApi,
  ApiProvider: ServiceProviderReceiptUploadPhoenixApiProvider,
} = makeApiProvider({
  name: 'ServiceProviderReceiptUploadPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ServiceProviderReceiptUploadPhoenixApi(tenantId, auth, apiUrl),
});

export {
  useServiceProviderReceiptUploadPhoenixApi,
  ServiceProviderReceiptUploadPhoenixApiProvider,
  ApiContext as ServiceProviderReceiptUploadPhoenixApiContext,
};

export type {
  IServiceProviderReceiptUploadPhoenixApi,
  GetUploadUrlBody,
  GetUploadUrlResult,
  GetReceiptUrlsBody,
  GetReceiptUrlsResult,
  ReceiptImageAnalysis,
} from './api';

/**
 * Returns a callback that runs receipt image analysis for an uploaded image.
 *
 * @example
 * ```tsx
 * const analyze = useAnalyzeReceiptImagePhoenix();
 * const result = await analyze('550e8400-...', 'test_tenant');
 * ```
 */
export const useAnalyzeReceiptImagePhoenix = (): ((
  imageId: string,
  tenant: string
) => Promise<ReceiptImageAnalysis>) => {
  const api = useServiceProviderReceiptUploadPhoenixApi();
  return useCallback(
    (imageId: string, tenant: string) =>
      api.analyzeReceiptImage(imageId, { tenant }),
    [api]
  );
};

/**
 * Hook that returns a callback function to get a presigned upload URL (Phoenix version)
 *
 * @param tenant - The tenant identifier
 * @returns Callback function that accepts receipt ID, file type, and MD5 hash
 *
 * @example
 * ```tsx
 * const getUploadUrl = useGetUploadUrlPhoenix({ tenant: 'test_tenant' });
 * const url = await getUploadUrl(
 *   '550e8400-e29b-41d4-a716-446655440000',
 *   'image/jpeg',
 *   'abc123=='
 * );
 * ```
 */
export const useGetUploadUrlPhoenix: ({
  tenant,
}: {
  tenant: string;
}) => (
  receiptId: string,
  fileType: string,
  md5Hash: string
) => Promise<string> = ({ tenant }: { tenant: string }) => {
  const api = useServiceProviderReceiptUploadPhoenixApi();
  return useCallback(
    (receiptId: string, fileType: string, md5Hash: string) => {
      return api.getUploadUrl(receiptId, {
        tenant,
        fileType,
        md5Hash,
      });
    },
    [api, tenant]
  );
};

const RECEIPT_IMAGE_URLS_AND_TYPE_KEY = (imageIds?: string[]) => [
  'RECEIPT_IMAGE_URLS_AND_TYPE_PHOENIX_KEY',
  { imageIds },
];

/**
 * Query hook that fetches presigned URLs and determines file types for receipt images (Phoenix version)
 *
 * @param imageIds - Array of image UUIDs
 * @param tenant - The tenant identifier
 * @returns Query result with array of objects containing image ID, URI, and file type
 *
 * @example
 * ```tsx
 * const { data: imagesWithTypes } = useReceiptImageUrlsAndTypePhoenix(
 *   ['550e8400-e29b-41d4-a716-446655440001'],
 *   'test_tenant'
 * );
 * // Returns: [{ imageId: '550e...', imageUri: 'https://...', fileType: 'image/jpeg' }]
 * ```
 */
export const useReceiptImageUrlsAndTypePhoenix = (
  imageIds: string[] | undefined,
  tenant: string
): UseQueryResult<
  Array<{
    imageId: string;
    imageUri?: string;
    fileType?: string;
  }>,
  unknown
> => {
  return useApiQuery(
    useServiceProviderReceiptUploadPhoenixApi,
    RECEIPT_IMAGE_URLS_AND_TYPE_KEY(imageIds),
    async (api) => {
      if (imageIds && imageIds.length > 0) {
        const imageIdsToUris = await api.getReceiptUrls({
          tenant,
          receiptIds: imageIds,
        });

        const imageIdsToTypes = await Promise.all(
          imageIds.map(async (imageId) => {
            const imageUri = imageIdsToUris?.[imageId];
            if (!imageUri) {
              return {
                imageId,
                imageUri: undefined,
                fileType: undefined,
              };
            }

            const response = await fetch(imageUri);
            const extractedFileType =
              response.headers.get('content-type') ?? 'type-unknown';

            return {
              imageId,
              imageUri,
              fileType: extractedFileType,
            };
          })
        );

        return imageIdsToTypes;
      }
      return [];
    }
  );
};
